import { useState, useCallback, useEffect, type RefObject } from 'react'

type UseScrollGradientsResult = {
  showTopGradient: boolean
  showBottomGradient: boolean
}

export const useScrollGradients = (
  containerRef: RefObject<HTMLElement>,
  horizontal?: boolean,
): UseScrollGradientsResult => {
  const [showTopGradient, setShowTopGradient] = useState(false)
  const [showBottomGradient, setShowBottomGradient] = useState(false)

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return
    const container = containerRef.current

    if (horizontal) {
      const { scrollLeft, scrollWidth, clientWidth } = container
      setShowTopGradient(scrollLeft > 0)
      setShowBottomGradient(scrollLeft + clientWidth < scrollWidth)
    } else {
      const { scrollTop, scrollHeight, clientHeight } = container
      setShowTopGradient(scrollTop > 0)
      setShowBottomGradient(scrollTop + clientHeight < scrollHeight)
    }
  }, [containerRef, horizontal])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    handleScroll()

    container.addEventListener('scroll', handleScroll)
    const resizeObserver = new ResizeObserver(handleScroll)
    resizeObserver.observe(container)

    return () => {
      container.removeEventListener('scroll', handleScroll)
      resizeObserver.disconnect()
    }
  }, [containerRef, handleScroll, horizontal])

  return { showTopGradient, showBottomGradient }
}
